/**
 * <product-bundle> — the fixed / dynamic bundle surface (P7).
 *
 * Reads its configuration from the [data-bundle-configuration] JSON the
 * Liquid surface emits (snippets/product-bundle-config.liquid, rendered
 * through the legacy-data adapter). The legacy-prefixed cart property keys of the
 * frozen bundle contract (D-05) arrive in `configuration.property_keys` — this
 * file never spells them (the O-07 lesson: live hand-built one of these keys
 * in JS, malformed it, and the property silently never reached the cart).
 *
 * Add path, matching live byte-for-byte where the wire contract cares:
 *  1. _collectItems() builds one component line per selected slot (plus the
 *     fixed components), each carrying `_bundle: "item"`, the token/hash pair,
 *     the legacy _ed* keys and the _sheko* grouping keys. Under the live
 *     default (enable_sheko_cart_transform) there is NO master line — the
 *     `product-bundle-v2` Cart Transform merges the components server-side.
 *  2. _authorizeShekoBundle() POSTs the manifest request (contractVersion 5)
 *     to the app proxy, validates the response shape strictly, and stamps the
 *     signed keys onto every component. One request, no retry — live retries
 *     nothing here, and a failed manifest ABORTS the add.
 *  3. The items go to routes.cart_add_url with the cart sections to render;
 *     P6's cart (drawer / notification) renders the result.
 *
 * Dependencies (all load-order independent): fetchConfig/routes from
 * global.js, the cart-drawer / cart-notification elements from P6.
 */
(() => {
  if (customElements.get('product-bundle')) return;

  class ProductBundle extends HTMLElement {
    constructor() {
      super();
      this._loading = true;
      try {
        const configElement = this.querySelector('[data-bundle-configuration]');
        if (configElement) {
          this.configuration = JSON.parse(configElement.innerText);
        } else {
          console.error('data-bundle-configuration element not found!');
          return;
        }
      } catch (error) {
        console.error('Error parsing configuration:', error);
        return;
      }
      this.propertyKeys = this.configuration.property_keys || {};
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.moneyFormat = document.body.getAttribute('data-money-format');
      this.bundleContent = this.querySelector('[data-bundle-content]');
      this.modal = this.querySelector('#BundleProductModal') || this.querySelector('modal-bundle-dialog:not(.additional-bundle-modal)');
      this.addVariantButtons = this.querySelectorAll('[data-add-variant]');
      this.quantity = this.querySelector('[data-bundle-quantity]');
      this.addToCart = this.querySelector('[data-add-to-cart]');
      this.setupAddToCartButtonDelegation();
      this.deliveryDuration = this.querySelector('[data-delivery-duration]');
      this.deliveryLoading = this.querySelector('[data-delivery-loading]');
      this.deliverySoldOut = this.querySelectorAll('[data-delivery-sold-out]');
      this.deliveryAvailable = this.querySelector('[data-delivery-available]');
      this.scrollTarget = this.querySelector('.bundle-scroll-target');
      this.addonToggle = this.querySelector('[data-bundle-addon-toggle]');
      this.isDynamicBundle = this.querySelector('[data-is-dynamic-bundle]');
      this.dynamicBundles = this.querySelectorAll('.bundle-variant-options .product-bundle__product');
      this.priceContainer = this.querySelector('.bundle-price-slot__price .price__container');
      document.addEventListener('bundle:cart:submit', this.handleAddToCart.bind(this));
      this.attachVariantButtons();
      this.init();
    }

    connectedCallback() {
      this.updateAnchorOffset = () => {
        const header = document.querySelector('.section-header');
        const style = header && getComputedStyle(header);
        const pinned = style && ['sticky', 'fixed'].includes(style.position) && style.top !== 'auto';
        const height = pinned && header.getClientRects().length
          ? header.getBoundingClientRect().height + (parseFloat(style.top) || 0)
          : 0;
        this.style.setProperty('--taste-selector-header-height', `${Math.max(0, height)}px`);
      };
      this.updateAnchorOffset();
      this.repairTasteSelectorLinks = () => {
        const targets = document.querySelectorAll('[data-taste-selector]');
        const target = this.querySelector('[data-taste-selector]');
        // A stale template ID is only unambiguous on a page with one selector.
        if (!target?.id || targets.length !== 1) return;
        const isStaleAnchor = (hash) =>
          /^#taste-selector(?:-template--[\w-]+)?$/.test(hash) &&
          !document.getElementById(hash.slice(1));
        for (const link of document.querySelectorAll('a[href]')) {
          const url = new window.URL(link.href, window.location.href);
          if (url.origin === window.location.origin &&
              url.pathname === window.location.pathname &&
              url.search === window.location.search && isStaleAnchor(url.hash)) {
            url.hash = target.id;
            link.href = url.href;
          }
        }
        if (isStaleAnchor(window.location.hash)) {
          this.updateAnchorOffset();
          target.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      };
      this.repairTasteSelectorLinks();
      document.addEventListener('DOMContentLoaded', this.repairTasteSelectorLinks);
      window.addEventListener('load', this.repairTasteSelectorLinks);
      window.addEventListener('hashchange', this.repairTasteSelectorLinks);
      window.addEventListener('resize', this.updateAnchorOffset);
      const header = document.querySelector('.section-header');
      if (header && typeof ResizeObserver !== 'undefined') {
        this.anchorHeaderObserver = new ResizeObserver(this.updateAnchorOffset);
        this.anchorHeaderObserver.observe(header);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateAnchorOffset);
      this.anchorHeaderObserver?.disconnect();
      document.removeEventListener('DOMContentLoaded', this.repairTasteSelectorLinks);
      window.removeEventListener('load', this.repairTasteSelectorLinks);
      window.removeEventListener('hashchange', this.repairTasteSelectorLinks);
    }

    set loading(loading) {
      this._loading = loading;

      if (loading) {
        this.bundleContent.classList.add('hidden');
      } else {
        this.bundleContent.classList.remove('hidden');
        this.updateDeliveryInfo();
      }
    }

    get loading() {
      return this._loading;
    }

    attachVariantButtons() {
      this.addVariantButtons.forEach((variantAddButton) => {
        variantAddButton.addEventListener('click', this.handleSelectProduct.bind(this));
      });
    }

    async init() {
      this.errors = [];
      this.selectedProducts = [];
      this.applyPreselection();
      this.updateAddToCartButton();
      if (this.scrollTarget) {
        this.scrollToFirstOpenSlot();
      }
      if (this.isDynamicBundle && this.dynamicBundles.length > 0) {
        const bindDynamic = () => this.dynamicBundles.forEach((bundle) => this.setupBundleClick(bundle));
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', bindDynamic);
        } else {
          bindDynamic();
        }
      }
      document.addEventListener('click', (event) => {
        const tooltips = document.querySelectorAll('.tooltip');
        const isClickInsideTooltip = Array.from(tooltips).some((tooltip) => tooltip.contains(event.target));
        const isClickOnLink = event.target.closest('[data-product-id]');
        if (!isClickInsideTooltip && !isClickOnLink) {
          tooltips.forEach((tooltip) => {
            tooltip.style.display = 'none';
          });
        }
      });
      this.querySelectorAll('.tooltip-container').forEach((container) => {
        const link = container.querySelector('.more-info-link');
        const tooltip = container.querySelector('.tooltip');
        if (!link || !tooltip) return;
        const showTooltip = () => {
          tooltip.style.display = 'block';
        };
        const hideTooltip = () => {
          tooltip.style.display = 'none';
        };
        link.addEventListener('mouseover', showTooltip);
        link.addEventListener('mouseout', hideTooltip);
        tooltip.addEventListener('mouseover', showTooltip);
        tooltip.addEventListener('mouseout', hideTooltip);
      });
      this.loading = false;
    }

    /* ------------------------------------------------------------------ *
     * Dynamic bundle (pack-size radios)
     * ------------------------------------------------------------------ */

    setupBundleClick(bundle) {
      bundle.addEventListener('click', (event) => {
        if (event.target.closest('input[type="radio"]')) return;
        const radio = bundle.querySelector('input[type="radio"]');
        if (radio && !radio.checked) {
          radio.checked = true;
          const dynamicBundleSize = radio.value;
          const variantPrice = radio.dataset.variantPrice;
          const variantComparePrice = radio.dataset.variantComparePrice;
          const variantPriceRaw = radio.dataset.variantPriceRaw;
          const variantComparePriceRaw = radio.dataset.variantComparePriceRaw;
          const variantUnitPrice = radio.dataset.variantUnitPrice;
          const variantUnitPriceMeasurement = radio.dataset.variantUnitPriceMeasurement;
          this.updateBundleContent(dynamicBundleSize);
          this.updateGalleryForVariant(radio.dataset.variantId);
          if (variantPrice && variantComparePrice) {
            this.updatePrice(variantPrice, variantComparePrice, variantPriceRaw, variantComparePriceRaw, variantUnitPrice, variantUnitPriceMeasurement);
          } else if (variantPrice) {
            // Live's no-compare branch mis-ordered its arguments, so only the
            // regular price updated. Same rendered behaviour, stated plainly.
            this.updatePrice(variantPrice, null, null, null, null, null);
          }
          this._isValidCartAdd();
          this.updateAddToCartButton();
        }
        this.dynamicBundles.forEach((item) => {
          if (event.currentTarget === item) {
            item.classList.add('selected');
          } else {
            item.classList.remove('selected');
          }
        });
      });
    }

    updateGalleryForVariant(variantId) {
      if (!variantId) return;
      const variants = Array.isArray(this.configuration.product?.variants)
        ? this.configuration.product.variants
        : [];
      const variant = variants.find((candidate) => String(candidate.id) === String(variantId));
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return;
      const gallery = document.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      const slide = gallery.querySelector(`[data-media-id$="-${mediaId}"]`);
      if (slide) gallery.setActiveMedia(slide.dataset.mediaId, true);
    }

    updatePrice(variantPrice, variantComparePrice, variantPriceRaw, variantComparePriceRaw, variantUnitPrice, variantUnitPriceMeasurement) {
      if (!this.priceContainer) return;
      if (variantPrice && variantComparePrice) {
        const priceElement = this.priceContainer.querySelector('.price__sale');
        if (priceElement) {
          const saleItem = priceElement.querySelector('.price-item--sale');
          const regularItem = priceElement.querySelector('.price-item--regular');
          if (saleItem) saleItem.innerText = variantPrice;
          if (regularItem) regularItem.innerText = variantComparePrice;
          const savingsBadge = priceElement.querySelector('.total-savings-badge');
          if (savingsBadge) {
            savingsBadge.innerText = formatBundleMoney(variantPriceRaw - variantComparePriceRaw, this.moneyFormat);
          }
        }
      } else if (variantPrice) {
        const priceElement = this.priceContainer.querySelector('.price__regular');
        if (priceElement) {
          const regularItem = priceElement.querySelector('.price-item--regular');
          if (regularItem) regularItem.innerText = variantPrice;
        }
      }

      if (variantUnitPrice && variantUnitPriceMeasurement) {
        const unitPriceContainer = this.priceContainer.querySelector('.unit-price');
        if (unitPriceContainer) {
          const unitPriceElement = unitPriceContainer.querySelector('.igPrice');
          const measurementElement = unitPriceContainer.querySelector('.price-item--last > span:last-child');
          if (unitPriceElement && measurementElement) {
            unitPriceElement.innerText = variantUnitPrice;
            measurementElement.innerText = variantUnitPriceMeasurement;
          }
        }
      }
    }

    updateBundleContent(dynamicBundleSize) {
      this.configuration.size = dynamicBundleSize;
      // The second selection group's slots (D-41) are not pack-sized and are
      // not main picks.
      const bundleItems = this.bundleContent.querySelectorAll(
        '.product-bundle__product:not(.product-bundle__product--additional)'
      );

      bundleItems.forEach((item) => {
        const current = parseInt(item.getAttribute('data-current'), 10);
        if (current <= parseInt(dynamicBundleSize, 10)) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      });

      const selectedElements = this.bundleContent.querySelectorAll(
        '.product-bundle__product[data-type="selected"]:not(.hidden):not(.product-bundle__product--additional)[data-product-variant-id]'
      );
      this.selectedProducts = Array.from(selectedElements)
        .filter((element) => element.getAttribute('data-product-variant-id').trim() !== '')
        .map((element) => element.getAttribute('data-product-variant-id'));
    }

    /* ------------------------------------------------------------------ *
     * Selection state
     * ------------------------------------------------------------------ */

    _isValidCartAdd() {
      const scope = this.querySelector('[data-bundle-content]') || this;
      const defaultSlots = scope.querySelectorAll(':scope > [data-type="default"]');
      const hasUnavailableProducts = scope.querySelectorAll(':scope > .product-bundle__product--unavailable').length > 0;

      if (defaultSlots.length === 0 && !hasUnavailableProducts) return true;

      return !hasUnavailableProducts && this.selectedProducts.filter((value) => value != null).length === parseInt(this.configuration.size, 10);
    }

    cleanProductTitle(title) {
      return title
        .replace('Shakemahlzeit ', '')
        .replace('Balance Shake ', '')
        .replace('sostitutivo del pasto alla ', '')
        .replace('sostitutivo del pasto al ', '')
        .replace('Shake sostitutivo del pasto ', '')
        .replace('sostitutivo del pasto ', '')
        .replace('Cura dimagrante ', '')
        .replace('Abnehmkur ', '');
    }

    parseImageData(data) {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (error) {
          console.error('Failed to parse bundle_products_with_images:', error);
          return null;
        }
      }
      return data;
    }

    handleSelectProduct(event) {
      // Rows of the second selection group's dialog belong to
      // bundle-additional-set.js (D-41): a product both lists share would
      // otherwise also land in the main slot opened last.
      if (event.target.closest('.additional-bundle-modal')) return;
      try {
        const productId = event.target.dataset.id || event.target.closest('[data-id]').dataset.id;
        const currentTarget = this.modal.dataset.currentItem;

        this._fillSlot(currentTarget, productId);
        document.body.classList.remove('overflow-hidden');

        if (typeof this.modal.hide === 'function') {
          this.modal.hide();
        } else {
          this.modal.removeAttribute('open');
        }
        this.modal.style.display = 'none';

        this.querySelectorAll('.promo-bundle-popup__flavor-modal[open], modal-bundle-dialog[open]').forEach((openModal) => {
          openModal.removeAttribute('open');
          openModal.style.display = 'none';
          if (typeof openModal.hide === 'function') openModal.hide();
        });

        this.updateAddToCartButton();
      } catch (error) {
        console.error('[handleSelectProduct] ERROR:', error);
      }
    }

    /**
     * Fills one selection slot with a component variant — the shared step of
     * a shopper's pick (handleSelectProduct) and the block's preselection.
     * Returns false when the slot or the component cannot be resolved.
     */
    _fillSlot(currentTarget, productId) {
      const bundleProduct = this.configuration.bundle_products.find(
        (candidate) => candidate.variants[0].id == productId
      );
      const targetContainer = this.querySelector(
        `[data-bundle-content] > .product-bundle__product[data-current="${currentTarget}"]:not(.product-bundle__product--additional)`
      ) || this.querySelector(`.product-bundle__product[data-current="${currentTarget}"]`);
      if (!bundleProduct || !targetContainer) return false;

      const selectedProductContainer = targetContainer.querySelector('[data-selected-product]');
      selectedProductContainer.innerText = this.cleanProductTitle(bundleProduct.title);

      const bundleProductsWithImages = this.parseImageData(this.configuration.bundle_products_with_images);
      if (bundleProductsWithImages && Array.isArray(bundleProductsWithImages)) {
        const selectedProductImage = targetContainer.querySelector('img');
        const bundleProductWithImage = bundleProductsWithImages.find((candidate) => candidate.id == bundleProduct.id);
        if (bundleProductWithImage?.fixed_image_preview_png) {
          selectedProductImage.src = bundleProductWithImage.fixed_image_preview_png;
        } else if (bundleProduct.featured_image) {
          selectedProductImage.src = bundleProduct.featured_image.src;
        }
      }

      targetContainer.dataset.type = 'selected';
      targetContainer.dataset.productVariantId = productId;
      this.selectedProducts[parseInt(currentTarget, 10) - 1] = productId;
      return true;
    }

    /**
     * Preselected flavours (block setting `preselected_flavors`, modal UI).
     * Slot n gets the n-th preselected flavour. A sold-out (or unlisted)
     * flavour is replaced by the next available one after it in the bundle's
     * flavour list, wrapping around and preferring flavours not already
     * preselected. Availability is read off the dialog rows: the list builder
     * only marks available components with [data-add-variant].
     */
    applyPreselection() {
      const preselected = this.configuration.preselected_products;
      const components = this.configuration.bundle_products;
      if (!Array.isArray(preselected) || preselected.length === 0) return;
      if (!Array.isArray(components) || components.length === 0 || !this.modal) return;
      if (!this.querySelector('[data-bundle-content] > .product-bundle__product[data-type="default"]')) return;

      const availableVariantIds = new Set(
        Array.from(this.modal.querySelectorAll('.product-bundle__product[data-add-variant][data-id]'))
          .map((row) => String(row.dataset.id))
      );
      const isAvailable = (component) => availableVariantIds.has(String(component.variants?.[0]?.id));
      const indexOfProduct = (productId) => components.findIndex((candidate) => String(candidate.id) === String(productId));
      const preselectedIds = new Set(preselected.map(String));

      const nextAvailable = (startIndex) => {
        const ordered = [];
        for (let offset = 1; offset <= components.length; offset += 1) {
          ordered.push(components[(startIndex + offset) % components.length]);
        }
        const candidates = ordered.filter(isAvailable);
        return candidates.find((candidate) => !preselectedIds.has(String(candidate.id))) || candidates[0] || null;
      };

      const slotCount = parseInt(this.configuration.size, 10) || 0;
      preselected.slice(0, slotCount).forEach((productId, index) => {
        const listIndex = indexOfProduct(productId);
        const component = listIndex >= 0 && isAvailable(components[listIndex]) ? components[listIndex] : nextAvailable(listIndex);
        if (component) this._fillSlot(index + 1, component.variants[0].id);
      });
    }

    updateDeliveryInfo() {
      if (!this.deliveryLoading) return;
      const allProductsUnavailable =
        this.querySelectorAll('.product-bundle__product--unavailable').length ===
        this.querySelectorAll('.product-bundle__product').length;

      this.deliveryLoading.classList.add('hidden');

      if (allProductsUnavailable) {
        this.deliveryDuration.classList.add('sold-out');
        this.deliverySoldOut.forEach((element) => element.classList.remove('hidden'));
        document.querySelectorAll('.bundle-button').forEach((element) => element.classList.add('hidden'));
      } else if (this.deliveryAvailable) {
        this.deliveryAvailable.classList.remove('hidden');
      }
    }

    updateAddToCartButton() {
      const bundleDrawer = document.querySelector('#BundleDrawer');
      this.addToCart.disabled = bundleDrawer ? false : !this._isValidCartAdd();
      if (bundleDrawer) this.addToCart.classList.remove('loading');
      this.emitStickyAddToCartButtonState();
    }

    emitStickyAddToCartButtonState() {
      document.dispatchEvent(
        new CustomEvent('bundle:cart:update_submit', {
          bubbles: true,
          detail: { disabled: this.addToCart.disabled },
        })
      );
    }

    setupAddToCartButtonDelegation() {
      if (this.hasAddToCartListener) return;
      this.hasAddToCartListener = true;

      this.addEventListener(
        'click',
        (event) => {
          const target = event.target.closest('[data-add-to-cart]');
          if (!target || target.disabled) return;

          event.preventDefault();
          event.stopPropagation();

          const drawer = document.querySelector('#BundleDrawer');
          drawer ? drawer.open() : this.handleAddToCart(event);
        },
        true
      );
    }

    /* ------------------------------------------------------------------ *
     * Add to cart
     *
     * Failure handling (2026-09-06, "stuck spinner" incident): the add used
     * to disable the button, show the spinner and only ever undo that on
     * success. Any non-2xx from /cart/add (e.g. Shopify's 422 when the
     * bundle Cart Transform Function fails) left a dead, spinning button
     * until the page was reloaded. Now:
     *   1. a failed add is retried ONCE automatically after a short pause
     *      (transient network / proxy hiccups resolve themselves);
     *   2. if it still fails, the button is restored with the selection
     *      intact and a plain-language message with a big "try again"
     *      button and a link to the cart is shown next to the buy button;
     *   3. the rendering step after a successful add sits in the same
     *      try/catch, so an exception there cannot strand the button;
     *   4. the tracking publish is capped so a hanging analytics promise
     *      cannot block the cart drawer.
     * ------------------------------------------------------------------ */

    static get ADD_RETRY_DELAY_MS() {
      return 1500;
    }

    static get TRACKING_PUBLISH_CAP_MS() {
      return 2500;
    }

    _setAddToCartBusy(busy) {
      if (busy) {
        this.addToCart.disabled = true;
        this.addToCart.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.addToCart.classList.add('loading');
      } else {
        const bundleDrawer = document.querySelector('#BundleDrawer');
        this.addToCart.disabled = bundleDrawer ? false : !this._isValidCartAdd();
        this.addToCart.classList.remove('loading');
        this.addToCart.querySelector('.loading__spinner')?.classList.add('hidden');
      }
      this.emitStickyAddToCartButtonState();
    }

    _errorCopy() {
      // Themes without the locale keys (the CH theme owns its own locales)
      // would hand us Shopify's "Translation missing: …" text; treat that as
      // absent and fall back to the German copy.
      const data = this.addToCart.dataset;
      const text = (value) => (value && !/^Translation missing/i.test(value) ? value : '');
      return {
        title: text(data.errorTitle) || 'Das hat leider nicht geklappt.',
        text: text(data.errorText) || 'Bitte versuche es noch einmal – Deine Auswahl ist gespeichert.',
        retry: text(data.errorRetry) || 'Noch einmal versuchen',
        cart: text(data.errorCart) || 'Zum Warenkorb',
        close: text(data.errorClose) || 'Schließen',
        help: text(data.errorHelp) || 'Klappt es weiterhin nicht? Wir helfen Dir gern:',
        contactLabel: data.errorContactLabel || '',
        contactHref: data.errorContactHref || '',
      };
    }

    /**
     * The failure surface is a centered <dialog>, not an inline note: the buy
     * box is position: sticky and taller than a laptop viewport, so anything
     * appended below the button is invisible until the page end. A dialog is
     * seen on every screen size and needs one tap to act on. Browsers without
     * <dialog>.showModal() (Safari < 15.4) get the same box inline, inserted
     * ABOVE the button where it is still on screen.
     */
    _renderAddToCartError() {
      if (this._errorBox) return this._errorBox;
      let box;

      const copy = this._errorCopy();
      const useDialog = typeof HTMLDialogElement === 'function' && 'showModal' in HTMLDialogElement.prototype;
      box = document.createElement(useDialog ? 'dialog' : 'div');
      box.setAttribute('data-bundle-error', '');
      box.setAttribute('aria-labelledby', 'BundleAddErrorTitle');
      box.className = `bundle-add-error${useDialog ? '' : ' bundle-add-error--inline'}`;
      if (!useDialog) {
        box.setAttribute('role', 'alert');
        box.setAttribute('tabindex', '-1');
        box.hidden = true;
      }

      const title = document.createElement('p');
      title.id = 'BundleAddErrorTitle';
      title.className = 'bundle-add-error__title';
      title.textContent = copy.title;

      const text = document.createElement('p');
      text.className = 'bundle-add-error__text';
      text.textContent = copy.text;

      const actions = document.createElement('div');
      actions.className = 'bundle-add-error__actions';

      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button bundle-add-error__retry';
      retry.textContent = copy.retry;
      retry.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this._hideAddToCartError();
        this.handleAddToCart();
      });
      actions.appendChild(retry);

      const cartLink = document.createElement('a');
      cartLink.className = 'bundle-add-error__cart link';
      cartLink.href = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
      cartLink.textContent = copy.cart;
      cartLink.hidden = true;
      cartLink.addEventListener('click', (event) => {
        if (this.cart && typeof this.cart.open === 'function') {
          event.preventDefault();
          this._hideAddToCartError();
          this.cart.open(cartLink);
        }
      });
      actions.appendChild(cartLink);

      box.append(title, text, actions);

      if (copy.contactHref) {
        const help = document.createElement('p');
        help.className = 'bundle-add-error__help';
        help.hidden = true;
        help.append(`${copy.help} `);
        const contact = document.createElement('a');
        contact.className = 'link';
        contact.href = copy.contactHref;
        contact.textContent = copy.contactLabel || copy.contactHref.replace(/^(mailto|tel):/, '');
        help.appendChild(contact);
        box.appendChild(help);
      }

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'bundle-add-error__close link';
      close.textContent = copy.close;
      close.addEventListener('click', (event) => {
        event.preventDefault();
        this._hideAddToCartError();
      });
      box.appendChild(close);

      if (useDialog) {
        // Closing via Escape or the backdrop must not leave the page in a stale state.
        box.addEventListener('click', (event) => {
          if (event.target === box) this._hideAddToCartError();
        });
        document.body.appendChild(box);
      } else {
        this.addToCart.insertAdjacentElement('beforebegin', box);
      }
      this._errorBox = box;
      return box;
    }

    async _showAddToCartError() {
      const box = this._renderAddToCartError();
      this._addErrorCount = (this._addErrorCount || 0) + 1;

      // "Zum Warenkorb" only makes sense when there is something in it.
      const cartLink = box.querySelector('.bundle-add-error__cart');
      if (cartLink) {
        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((response) => (response.ok ? response.json() : null));
          cartLink.hidden = !(cart && cart.item_count > 0);
        } catch (error) {
          cartLink.hidden = true;
        }
      }

      // The support hint appears only after the customer's own retry failed too.
      const help = box.querySelector('.bundle-add-error__help');
      if (help) help.hidden = this._addErrorCount < 2;

      if (typeof box.showModal === 'function') {
        box.hidden = false;
        if (!box.open) box.showModal();
        box.querySelector('.bundle-add-error__retry')?.focus();
        return;
      }
      box.hidden = false;
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        box.focus({ preventScroll: true });
      } catch (error) {
        /* focus is a courtesy */
      }
    }

    _hideAddToCartError() {
      const box = this._errorBox;
      if (!box) return;
      if (typeof box.close === 'function') {
        // Never toggle `hidden` on a <dialog>: it would survive the next showModal().
        if (box.open) box.close();
        return;
      }
      box.hidden = true;
    }

    async handleAddToCart(event, options = {}) {
      if (!this._isValidCartAdd()) return;
      if (this._addInFlight && !options.retry) return;
      this._addInFlight = true;

      const isRetry = options.retry === true;
      if (!isRetry) this._hideAddToCartError();
      this._setAddToCartBusy(true);
      const config = fetchConfig();
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const fail = async (phase, error) => {
        this._appendError(phase, error);
        console.warn(`Bundle add failed (${phase}).`, error);
        if (!isRetry) {
          // One quiet automatic retry: most failures are momentary.
          await new Promise((resolve) => setTimeout(resolve, ProductBundle.ADD_RETRY_DELAY_MS));
          this._addInFlight = false;
          return this.handleAddToCart(null, { retry: true });
        }
        this._addInFlight = false;
        this._setAddToCartBusy(false);
        await this._showAddToCartError();
      };

      let items;
      try {
        items = this._collectItems();
        if (this.configuration.enable_sheko_cart_transform !== false) {
          await this._authorizeShekoBundle(items);
        }
      } catch (error) {
        return fail('handleAddToCart - bundle authorization', error);
      }

      // A redirect leaves the page, so the cart sections would be rendered by
      // Shopify for nothing — skip them and the add answers faster.
      const redirectsAway = this.configuration.redirect_to_checkout || this.configuration.redirect_to_cart;
      const formData = redirectsAway
        ? { items }
        : {
            items,
            sections: this.cart.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          };
      config.body = JSON.stringify(formData);

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        if (!response.ok) {
          // Keep Shopify's explanation (e.g. {"errors":"Warenkorb-Fehler"}) —
          // before only the status code survived into the error log.
          const body = await response.text().catch(() => '');
          throw new Error(`Bundle cart add failed (${response.status}): ${body.slice(0, 300)}`);
        }
        const payload = await response.json();
        const responseItems = Array.isArray(payload.items) ? payload.items : [payload];
        const addedItem =
          responseItems.find((item) => item.properties?._bundle === 'master') || responseItems[0] || {};

        try {
          this.bridgeNativeBundleAddToCart(formData.items, responseItems, addedItem);
        } catch (error) {
          console.warn('Shopify native bundle tracking bridge could not be published.', error);
        }

        // The cart-context write is independent of the tracking event, so it
        // runs alongside the tracking wait instead of after it.
        const contextWrite = this.configuration.redirect_to_checkout
          ? Promise.race([
              Promise.resolve(window.shekoTracking?.persistCartContext?.()).catch(() => {}),
              new Promise((resolve) => setTimeout(resolve, 1500)),
            ])
          : null;

        try {
          await Promise.race([
            this.publishTrackingEvent(formData.items),
            new Promise((resolve) => setTimeout(resolve, ProductBundle.TRACKING_PUBLISH_CAP_MS)),
          ]);
        } catch (error) {
          console.warn('Bundle tracking event could not be published.', error);
        }

        if (this.configuration.redirect_to_checkout) {
          await contextWrite;
          window.location.href = '/checkout';
          return;
        }

        if (this.configuration.redirect_to_cart) {
          window.location.href = '/cart';
          return;
        }

        let sections = payload.sections;
        if (!sections) {
          try {
            sections = await fetch(`/?sections=${formData.sections.join(',')}`).then((fallbackResponse) =>
              fallbackResponse.json()
            );
          } catch (error) {
            this._appendError('handleAddToCart - fallback', error);
            console.warn('Failed to fetch sections fallback.', error);
          }
        }

        if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        this.cart.renderContents({
          ...addedItem,
          sections: sections || formData.sections,
        });

        this._addErrorCount = 0;
        this.addToCart.classList.remove('loading');
        this.addToCart.querySelector('.loading__spinner')?.classList.add('hidden');

        if (this.addToCart.dataset.success && this.addToCart.querySelector('.add-disallowed')) {
          this.addToCart.classList.add('success');
          this.addToCart.dataset.text = this.addToCart.querySelector('.add-allowed').innerText;
          this.addToCart.querySelector('.add-disallowed').innerText = this.addToCart.dataset.success;
          this.emitCartUpdate();
          setTimeout(() => {
            this.addToCart.querySelector('.add-disallowed').innerText = this.addToCart.dataset.text;
            this.addToCart.classList.remove('success');
          }, 1500);
        }

        if (document.querySelector('#BundleDrawer')) {
          this.addToCart.disabled = false;
        }

        const promoDialog = this.closest('.promo-bundle-popup');
        if (promoDialog) promoDialog.close();

        this._addInFlight = false;
        await this.reset();
      } catch (error) {
        return fail('handleAddToCart', error);
      }
    }

    bridgeNativeBundleAddToCart(componentItems, responseItems, addedItem) {
      // Shopify's native product_added_to_cart detector can miss a bulk add
      // when Cart Transform replaces several component lines with one parent.
      // Only bridge that transformed shape; ordinary/untransformed adds keep
      // Shopify's own event and therefore cannot be double-counted here.
      if (
        !Array.isArray(componentItems)
        || !Array.isArray(responseItems)
        || componentItems.length === responseItems.length
      ) {
        return false;
      }

      const analytics = window.ShopifyAnalytics;
      if (typeof analytics?.lib?.track !== 'function') return false;

      const firstProperties = componentItems[0]?.properties || {};
      const variantId = addedItem.variant_id
        || addedItem.id
        || firstProperties._shekoBundleParentVariantId
        || firstProperties[this.propertyKeys.parent_variant];
      if (!variantId) return false;

      const page = analytics.meta?.page || {};
      const cartToken = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('cart='))
        ?.split('=')
        .slice(1)
        .join('=');
      const manifestPrice = Number(firstProperties._shekoBundlePrice);
      const responsePrice = Number(addedItem.presentment_price);
      const eventProperties = {
        ...(cartToken ? { cartToken: decodeURIComponent(cartToken) } : {}),
        variantId: String(variantId),
        productId: addedItem.product_id || this.configuration.product?.id,
        currency: addedItem.currency
          || analytics.meta?.currency
          || window.Shopify?.currency?.active,
        quantity: String(
          addedItem.quantity
          || firstProperties._shekoBundleQuantity
          || this.quantity?.value
          || 1
        ),
        price: Number.isFinite(responsePrice) ? responsePrice : manifestPrice,
        name: addedItem.title || addedItem.product_title || this.configuration.product?.title,
        sku: addedItem.sku || firstProperties._bundleSku || this.configuration.sku,
        brand: addedItem.vendor || this.configuration.product?.vendor,
        variant: addedItem.variant_title || '',
        category: addedItem.product_type || this.configuration.product?.type,
        remote: addedItem.remote || null,
        ...(page.pageType ? { pageType: page.pageType } : {}),
        ...(page.resourceType ? { resourceType: page.resourceType } : {}),
        ...(page.resourceId ? { resourceId: page.resourceId } : {}),
      };

      analytics.lib.track(
        'Added Product',
        eventProperties,
        undefined,
        undefined,
        {
          addApiSource: 'add-fetch-bulk-cart-transform-bridge',
          shopifyEmitted: true,
        }
      );
      return true;
    }

    /**
     * The signed-manifest step of the D-05 contract, request and validation
     * matching live exactly: one POST, strict shape check, no retry, and a
     * failure aborts the add. The response's signed values are stamped onto
     * every component line under their FROZEN property names.
     */
    async _authorizeShekoBundle(items) {
      const components = items.filter((item) => item.properties?._shekoBundleId);
      if (components.length < 2) {
        throw new Error('No complete SHEKO bundle was collected.');
      }
      const template = components[0];
      const bundleId = String(template.properties._shekoBundleId || '');
      const parentVariantId = String(template.properties._shekoBundleParentVariantId || '');
      const bundleQuantity = parseInt(this.quantity.value, 10);
      const country = this.configuration.country;
      if (!/^[A-Z]{2}$/.test(country || '')) {
        throw new Error('The buyer market could not be determined.');
      }
      const manifestUrl = this.configuration.bundle_manifest_url || '/apps/sheko-sales-analytics/bundle-manifest';
      const response = await fetch(manifestUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          contractVersion: 5,
          includeMarketPrices: true,
          country,
          bundleId,
          parentVariantId,
          bundleQuantity,
          components: components.map((item) => ({
            variantId: item.id,
            quantity: item.quantity,
          })),
        }),
      });
      const manifest = await response.json().catch(() => ({}));
      if (
        !response.ok
        || manifest.version !== 5
        || typeof manifest.signature !== 'string'
        || typeof manifest.sku !== 'string'
        || typeof manifest.price !== 'string'
        || !/^\d{1,12}(?:\.\d{1,6})?$/.test(manifest.price)
        || typeof manifest.currencyCode !== 'string'
        || !/^[A-Z]{3}$/.test(manifest.currencyCode)
        || manifest.country !== country
        || !/^\d{4}-\d{2}-\d{2}$/.test(manifest.validDate || '')
        || !/^[A-Za-z0-9-]{8,64}$/.test(manifest.keyId || '')
      ) {
        throw new Error(manifest.error || 'Bundle manifest request failed.');
      }
      // Optional for a staged rollout: old app versions still return v5 only.
      const hasMarketPrices = manifest.marketPrices !== undefined || manifest.marketPricesSignature !== undefined;
      if (hasMarketPrices && (
        typeof manifest.marketPrices !== 'string' || !manifest.marketPrices || manifest.marketPrices.length > 8192
        || typeof manifest.marketPricesSignature !== 'string' || !manifest.marketPricesSignature
      )) {
        throw new Error('Bundle destination prices are incomplete.');
      }
      components.forEach((item) => {
        if (hasMarketPrices) {
          item.properties._shekoBundleMarketPrices = manifest.marketPrices;
          item.properties._shekoBundleMarketPricesSignature = manifest.marketPricesSignature;
        }
        item.properties._shekoBundleQuantity = bundleQuantity;
        item.properties._shekoBundleSignature = manifest.signature;
        item.properties._shekoBundleValidDate = manifest.validDate;
        item.properties._shekoBundleKeyId = manifest.keyId;
        item.properties._shekoBundlePrice = manifest.price;
        item.properties._shekoBundleShopCurrency = manifest.currencyCode;
        item.properties._shekoBundleMarketCountry = manifest.country;
        item.properties._bundleSku = manifest.sku;
      });
    }

    emitCartUpdate() {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cartData) => {
          document.dispatchEvent(
            new CustomEvent('cart-quantity-updated', {
              bubbles: true,
              detail: { source: 'product-bundle', cartData },
            })
          );
        });
    }

    _collectItems() {
      const keys = this.propertyKeys;
      const items = [];
      const selectedProducts = [];
      const componentProductIds = [...this.selectedProducts];
      let selectedProductIds = [];
      const bundleInfo = {};
      const bundleId = globalThis.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      const selectedBundleRadio = this.querySelector('input[type="radio"]:checked');
      const parentVariantId = parseInt(selectedBundleRadio?.dataset.variantId || this.configuration.id, 10);
      const configuredVariants = Array.isArray(this.configuration.product?.variants)
        ? this.configuration.product.variants
        : [];
      const selectedParentVariant = configuredVariants.find(
        (variant) => parseInt(variant.id, 10) === parentVariantId
      );
      const selectedVariantConfiguration = this.configuration.variants_map?.[parentVariantId];
      const bundleSku = selectedParentVariant?.sku
        || selectedVariantConfiguration?.sku
        || this.configuration.sku
        || '';
      const bundlePrice = selectedParentVariant?.price
        || selectedVariantConfiguration?.price
        || this.configuration.parent_price;

      if (this.configuration.fixed_products && this.configuration.fixed_products.length) {
        this.configuration.fixed_products.forEach((fixedProduct) => {
          componentProductIds.push(fixedProduct.variants[0].id);
          if (bundleInfo[fixedProduct.variants[0].id] == undefined) {
            bundleInfo[fixedProduct.variants[0].id] = parseInt(this.quantity.value, 10);
          } else {
            bundleInfo[fixedProduct.variants[0].id] += parseInt(this.quantity.value, 10);
          }
        });
      }
      const sumQuantities = {};
      componentProductIds.forEach((productId) => {
        if (sumQuantities[productId] === undefined) {
          sumQuantities[productId] = 1;
        } else {
          sumQuantities[productId] += 1;
        }
      });
      if (this.configuration.bundle_products) {
        componentProductIds.forEach((productId) => {
          let bundleProduct = this.configuration.bundle_products.find(
            (candidate) => candidate.variants[0].id == productId
          );
          if (bundleProduct == undefined) {
            bundleProduct = this.configuration.fixed_products.find(
              (candidate) => candidate.variants[0].id == productId
            );
          }
          selectedProducts.push(`${bundleProduct.title}`);

          if (bundleProduct.variants?.length > 0) {
            selectedProductIds.push(bundleProduct.variants[0].id);
          }
        });
      }
      Object.keys(sumQuantities).forEach((productId) => {
        bundleInfo[productId] = parseInt(sumQuantities[productId], 10) * parseInt(this.quantity.value, 10);
      });

      if (!this.configuration.bundle_products && componentProductIds.length > 0) {
        componentProductIds.forEach((fixedProductId) => {
          selectedProductIds.push(fixedProductId);
        });
      }

      selectedProductIds = selectedProductIds.join(',');
      const masterProperties = {
        _bundle: 'master',
        _token: this.configuration.token,
        _hash: this.configuration.hash,
        _selected: selectedProducts,
        _bundleInfo: bundleInfo,
        _bundleSku: bundleSku,
        [keys.items]: selectedProductIds,
        [keys.master_id]: parentVariantId,
        [keys.group_id]: bundleId,
        [keys.master_quantity]: parseInt(this.quantity.value, 10),
      };

      const itemCount = selectedProducts.length > 0
        ? selectedProducts.length
        : this.configuration.fixed_products.length;
      masterProperties._bundleItemCount = itemCount * parseInt(this.quantity.value, 10);

      if (this.errors.length) {
        masterProperties['_errors'] = this.errors.join(' - ');
      }

      // The SHEKO integration is enabled by default. An explicit false keeps a
      // per-section emergency opt-out without requiring template migrations.
      const useShekoCartTransform = this.configuration.enable_sheko_cart_transform !== false;
      const enableLegacyCheckoutExtension = this.configuration.enable_checkout_extension === true;
      const enableLegacyBundleV2 = this.configuration.enable_checkout_extension_v2 === true;

      if (!useShekoCartTransform && (enableLegacyBundleV2 || !enableLegacyCheckoutExtension)) {
        items.push({
          quantity: parseInt(this.quantity.value, 10),
          id: parentVariantId,
          properties: masterProperties,
        });
      }

      const componentItems = useShekoCartTransform
        ? componentProductIds.map((productId, index) => ({
            productId,
            quantity: parseInt(this.quantity.value, 10),
            componentId: `${bundleId}-${index + 1}`,
          }))
        : Object.entries(sumQuantities).map(([productId, quantity]) => ({
            productId,
            quantity: parseInt(quantity, 10) * parseInt(this.quantity.value, 10),
            componentId: '',
          }));

      componentItems.forEach(({ productId, quantity, componentId }) => {
        const item = {
          quantity,
          id: parseInt(productId, 10),
          properties: {
            _bundle: 'item',
            _token: this.configuration.token,
            _hash: this.configuration.hash,
            _checkout_hidden: true,
          },
        };

        if (useShekoCartTransform) {
          // The legacy keys stay alongside the SHEKO cart-transform contract.
          // Every component must share the same group and parent.
          item.properties[keys.group_id] = bundleId;
          item.properties[keys.parent_variant] = parentVariantId;
          item.properties._shekoBundleId = bundleId;
          item.properties._shekoBundleParentVariantId = parentVariantId;
          item.properties._shekoBundleComponentId = componentId;
          item.properties[keys.price] = bundlePrice;
          item.properties[keys.quantity] = parseInt(this.quantity.value, 10);
          item.properties._bundleSku = bundleSku;
          item.properties._handle = this.configuration.handle;
        } else if (enableLegacyCheckoutExtension) {
          item.properties[keys.group_id] = bundleId;
          item.properties[keys.parent_variant] = parentVariantId;
          item.properties[keys.price] = bundlePrice;
          item.properties[keys.quantity] = parseInt(this.quantity.value, 10);
          item.properties._bundleSku = bundleSku;
          item.properties._handle = this.configuration.handle;
        }

        if (useShekoCartTransform || !enableLegacyBundleV2) {
          items.push(item);
        }
      });

      if (this.addonToggle?.checked) {
        const addonVariantId = parseInt(this.addonToggle.dataset.addonVariantId, 10);
        if (addonVariantId) {
          items.push({
            quantity: 1,
            id: addonVariantId,
            properties: { _upsell: 'true' },
          });
        }
      }

      return items;
    }

    scrollToFirstOpenSlot() {
      this.scrollTarget.addEventListener('click', () => {
        const allProducts = this.bundleContent.querySelectorAll('.product-bundle__product');
        const selectableProducts = Array.from(allProducts).filter(
          (product) => product.getAttribute('data-type') !== 'fixed' && product.getClientRects().length
        );

        for (const product of selectableProducts) {
          if (product.getAttribute('data-type') !== 'selected') {
            product.scrollIntoView({
              block: 'start',
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
            });
            break;
          }
        }
      });
    }

    async reset() {
      this.loading = true;
      try {
        const sectionUrl = new URL(window.location.pathname, window.location.origin);
        sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
        const responseText = await fetch(sectionUrl.toString()).then((response) => response.text());
        const newSection = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('[data-bundle-content]');
        if (newSection && newSection.innerHTML) {
          this.querySelector('[data-bundle-content]').innerHTML = newSection.innerHTML;
          this.bundleContent = this.querySelector('[data-bundle-content]');
          if (
            this.isDynamicBundle &&
            this.dynamicBundles.length > 0 &&
            this.dynamicBundles[0].querySelector('input[type="radio"]')
          ) {
            this.dynamicBundles[0].querySelector('input[type="radio"]').checked = true;
          }
          await this.refreshHash();
          this.attachVariantButtons();
          this.init();

          if (document.querySelector('#BundleDrawer')) {
            this.addToCart.disabled = false;
          }
        }
      } catch (error) {
        this._appendError('reset', error);
        console.warn('Bundle section could not be refreshed.', error);
      } finally {
        // A failed section re-fetch must never leave the bundle box hidden.
        if (this.loading) this.loading = false;
      }
    }

    async publishTrackingEvent(formData) {
      const trackingLine = formData.find((item) => item.properties?._shekoBundleId)
        || formData.find((item) => item.properties?._bundle === 'master')
        || formData[0];
      if (!trackingLine) return;

      const properties = trackingLine.properties || {};
      const bundleId = properties._shekoBundleId
        || properties[this.propertyKeys.group_id];
      const parentVariantId = Number(
        properties._shekoBundleParentVariantId
        || properties[this.propertyKeys.parent_variant]
        || this.configuration.id
      );
      const variants = Array.isArray(this.configuration.product?.variants)
        ? this.configuration.product.variants
        : [];
      const parentVariant = variants.find((variant) => Number(variant.id) === parentVariantId) || {};
      const quantity = Math.max(1, Number(this.quantity?.value) || 1);
      const unitPrice = Number(
        parentVariant.price
        || this.configuration.variants_map?.[parentVariantId]?.price
        || this.configuration.parent_price
        || 0
      ) / 100;
      const currency = window.Shopify?.currency?.active || 'EUR';
      const eventId = window.shekoTracking?.createEventId?.()
        || globalThis.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      const trackingContext = window.shekoTracking?.getContext?.() || {};
      const metaAddress = window.shekoTracking?.getMetaAddress?.() || {};
      const item = {
        item_id: `shopify_${window.Shopify?.country || 'DE'}_${this.configuration.product.id}_${parentVariantId}`,
        item_name: this.configuration.product.title,
        affiliation: 'SHEKO',
        currency,
        item_brand: this.configuration.product.vendor,
        item_category: this.configuration.product.type,
        item_variant: parentVariant.title || '',
        price: Number(unitPrice.toFixed(2)),
        quantity
      };
      const payload = {
        event_id: eventId,
        event_source: 'shopify_pixel',
        ...trackingContext,
        ...metaAddress,
        bundle_id: String(bundleId || ''),
        parent_variant_id: String(parentVariantId),
        component_count: formData.filter((line) => line.properties?._bundle === 'item').length,
        ecommerce: {
          currency,
          value: Number((unitPrice * quantity).toFixed(2)),
          items: [item]
        }
      };

      if (typeof window.Shopify?.analytics?.publish !== 'function') {
        throw new Error('Shopify analytics custom-event publisher is unavailable');
      }
      const dispatched = await Promise.resolve(
        window.Shopify.analytics.publish('sheko_bundle_added_to_cart', payload)
      );
      if (dispatched === false) {
        throw new Error('Shopify analytics rejected the bundle tracking event');
      }

      // Shopify confirms dispatch before a subscribed Customer Events pixel has
      // necessarily processed the event. Give the sandboxed pixel enough time to
      // push the canonical add_to_cart and let GTM send it before a direct
      // checkout redirect tears down the storefront page.
      // The Customer Events pixel starts its sandboxed GTM container lazily on
      // this event. 350 ms was not enough in production: the following direct
      // checkout navigation could tear the sandbox down before add_to_cart was
      // sent. Keep this delay local to direct bundle checkout, where preserving
      // the canonical event is more important than a sub-second redirect.
      await new Promise((resolve) => setTimeout(resolve, 1200));
    }

    async refreshHash() {
      try {
        await fetch(`${window.location.pathname}?view=hash`)
          .then((response) => response.json())
          .then((response) => {
            this.configuration.hash = response.hash;
            this.configuration.token = response.token;
          });
      } catch (error) {
        console.error(error);
      }
    }

    _appendError(type, error) {
      try {
        const errorData = {
          type,
          date: new Date(),
          navigator: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            connection: navigator.connection
              ? {
                  downlink: navigator.connection.downlink,
                  effectiveType: navigator.connection.effectiveType,
                }
              : null,
          },
          location: window.location.href,
          url: routes.cart_add_url,
          error: {
            message: error.message,
            stack: error.stack,
          },
        };
        this.errors.push(encodeURIComponent(btoa(JSON.stringify(errorData))));
      } catch (encodingError) {
        this.errors.push(`_appendError Exception: ${encodingError.message}`);
      }
    }
  }

  customElements.define('product-bundle', ProductBundle);

  function formatBundleMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '{{ amount }}';

    function formatWithDelimiters(number, precision = 2, thousands = '.', decimal = ',') {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    let value = '';
    switch (formatString.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }
})();
